import * as billingApi from '@/api/billing'

const DEFAULT_PLANS = [
  {
    slug: 'free',
    name: 'Free',
    description: 'Try Design Studio with API & MCP access',
    price: 0,
    currency: 'USD',
    interval: 'month',
    features: {
      max_storage_bytes: 104857600,
      max_canva_imports: 1,
      api_enabled: true,
      mcp_enabled: true,
      max_posts: 10,
      max_accounts: 1,
      ai_enabled: false,
      max_ai_tokens: 0,
      recurring: false,
      analytics: false,
      design_studio: true,
    },
  },
  {
    slug: 'pro',
    name: 'Pro',
    description: 'More Canva imports for active creators',
    price: 9,
    price_idr: 150000,
    currency: 'USD',
    interval: 'month',
    features: {
      max_storage_bytes: 5368709120,
      max_canva_imports: 10,
      api_enabled: true,
      mcp_enabled: true,
      max_posts: 100,
      max_accounts: 3,
      ai_enabled: true,
      max_ai_tokens: 100000,
      recurring: true,
      analytics: true,
      design_studio: true,
    },
  },
  {
    slug: 'business',
    name: 'Business',
    description: 'Unlimited Canva imports for teams and agencies',
    price: 24.9,
    price_idr: 400000,
    currency: 'USD',
    interval: 'month',
    features: {
      max_storage_bytes: -1,
      max_canva_imports: -1,
      api_enabled: true,
      mcp_enabled: true,
      max_posts: -1,
      max_accounts: 10,
      ai_enabled: true,
      max_ai_tokens: 1000000,
      recurring: true,
      analytics: true,
      design_studio: true,
    },
  },
]

const state = {
  plans: DEFAULT_PLANS,
  subscription: null,
  subscriptions: [],
  subscriptionsTotal: 0,
  config: { configured: false, paypal_configured: false, pakasir_configured: false },
  aiTokens: null,
  loading: false,
}

const getters = {
  plans: (state) => state.plans,
  subscription: (state) => state.subscription,
  subscriptions: (state) => state.subscriptions,
  subscriptionsTotal: (state) => state.subscriptionsTotal,
  config: (state) => state.config,
  loading: (state) => state.loading,
  paypalReady: (state) => !!state.config.paypal_configured,
  pakasirReady: (state) => !!state.config.pakasir_configured,
  paymentReady: (state) => !!(state.config.paypal_configured || state.config.pakasir_configured),
  currentPlanSlug: (state) => state.subscription?.plan?.slug || 'free',
  aiTokens: (state) => state.aiTokens,
}

const actions = {
  async fetchPlans({ commit }) {
    try {
      const { data } = await billingApi.listPlans()
      const plans = data.data.plans || []
      commit('SET_PLANS', plans.length ? plans : DEFAULT_PLANS)
      return plans
    } catch {
      commit('SET_PLANS', DEFAULT_PLANS)
      return DEFAULT_PLANS
    }
  },

  async fetchConfig({ commit }) {
    try {
      const { data } = await billingApi.getBillingConfig()
      commit('SET_CONFIG', data.data.config || { configured: false })
      return data.data.config
    } catch {
      commit('SET_CONFIG', { configured: false })
      return { configured: false }
    }
  },

  async fetchSubscription({ commit }) {
    try {
      const { data } = await billingApi.getSubscription()
      commit('SET_SUBSCRIPTION', data.data.subscription)
      commit('SET_AI_TOKENS', data.data.ai_tokens || null)
      return data.data.subscription
    } catch {
      commit('SET_SUBSCRIPTION', null)
      commit('SET_AI_TOKENS', null)
      return null
    }
  },

  async subscribe(_, { planSlug, provider }) {
    const { data } = await billingApi.subscribe(planSlug, provider)
    return data.data
  },

  async verifyPakasirPayment({ dispatch }, orderId) {
    const { data } = await billingApi.verifyPakasirPayment(orderId)
    await dispatch('fetchSubscription')
    return data.data
  },

  async fetchSubscriptions({ commit }, params = {}) {
    const { data } = await billingApi.listSubscriptions(params)
    commit('SET_SUBSCRIPTIONS', {
      subscriptions: data.data.subscriptions || [],
      total: data.data.total || 0,
    })
    return data.data
  },

  async cancelSubscription({ dispatch }) {
    await billingApi.cancelSubscription()
    await Promise.all([dispatch('fetchSubscription'), dispatch('fetchSubscriptions')])
  },

  async cancelSubscriptionById({ dispatch }, id) {
    await billingApi.cancelSubscriptionById(id)
    await Promise.all([dispatch('fetchSubscription'), dispatch('fetchSubscriptions')])
  },

  async resumePayment(_, id) {
    const { data } = await billingApi.resumeSubscriptionPayment(id)
    return data.data
  },

  async fetchSubscriptionDetail(_, id) {
    const { data } = await billingApi.getSubscriptionById(id)
    return data.data.subscription
  },

  async downloadInvoice(_, id) {
    const { data } = await billingApi.downloadSubscriptionInvoice(id)
    return data
  },

  async loadBilling({ commit, dispatch }) {
    commit('SET_LOADING', true)
    try {
      await Promise.all([
        dispatch('fetchPlans'),
        dispatch('fetchConfig'),
        dispatch('fetchSubscription'),
      ])
    } finally {
      commit('SET_LOADING', false)
    }
  },
}

const mutations = {
  SET_PLANS(state, plans) {
    state.plans = plans.length ? plans : DEFAULT_PLANS
  },
  SET_SUBSCRIPTION(state, subscription) {
    state.subscription = subscription
  },
  SET_AI_TOKENS(state, usage) {
    state.aiTokens = usage
  },
  SET_SUBSCRIPTIONS(state, { subscriptions, total }) {
    state.subscriptions = subscriptions
    state.subscriptionsTotal = total
  },
  SET_CONFIG(state, config) {
    state.config = config
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
